import type { TransportRouteDetailDto } from '../../dto/transport';
import type { AllocationMethod } from '../../utils/transportCostAllocation';
import { carriedLoadItems } from './supplierOrderFreight';
import { buildInboundCostSplitPreview, type InboundPreviewLine } from './transportCostSplitPreview';

/**
 * „Co gdyby klucz był inny?” on the truck card (owner 2026-10-06): the same
 * INBOUND preview the truck form shows (buildInboundCostSplitPreview), applied
 * to the saved truck — a copy of the route whose carried ZD lines carry the
 * previewed freight, so the trailer and the „Ładunek” table recompute.
 * Display only: the saved split stays the backend's until the key is saved
 * (transportService.syncTransportCostsInTransaction). The total is the sum of
 * the saved ZD shares in PLN, so the preview splits the same money.
 */
export function previewRouteWithKey(
  route: TransportRouteDetailDto,
  method: AllocationMethod,
  /** Goods value (PLN) of each whole ZD line — needed only by „wartość towaru”. */
  lineValuePLN: Map<string, number> | null,
): TransportRouteDetailDto | null {
  const shares = route.supplierOrders.map((supplierOrder) => supplierOrder.freightShare?.amountPLN ?? null);
  if (shares.length === 0 || shares.some((amount) => amount === null)) return null;
  const totalPLN = shares.reduce<number>((sum, amount) => sum + (amount as number), 0);

  const lines: InboundPreviewLine[] = route.supplierOrders.flatMap((supplierOrder) => carriedLoadItems(supplierOrder.loadItems)
    .map((item) => {
      const whole = (supplierOrder.loadItems || []).find((candidate) => candidate.id === item.id);
      const lineValue = lineValuePLN?.get(item.id);
      const valuePLN = lineValue != null && whole?.quantity && Number(item.quantity) > 0
        ? (lineValue * Number(item.quantity)) / whole.quantity
        : null;
      return {
        id: item.id,
        groupId: supplierOrder.id,
        orderLabel: supplierOrder.displayOrderNumber || supplierOrder.orderNumber,
        name: item.name,
        quantity: Number(item.quantity) || 0,
        unit: item.carried?.unit || item.unit || 'pcs',
        pcs: item.quantityPcs,
        m2: item.quantityM2,
        m3: item.quantityM3,
        width: item.width,
        height: item.height,
        length: item.length,
        thickness: item.thickness,
        countInPiecesOnly: item.countInPiecesOnly === true,
        valuePLN,
      };
    }));
  const rows = buildInboundCostSplitPreview({ lines, method, transportCost: totalPLN, rateToPLN: 1 });
  // A line the key cannot weigh gets 0 (the server's rule, with a warning).
  const freightById = new Map(rows.map((row) => [row.key, row.sharePercent === null ? 0 : row.amountPLN]));

  return {
    ...route,
    costAllocationMethod: method,
    supplierOrders: route.supplierOrders.map((supplierOrder) => ({
      ...supplierOrder,
      loadItems: (supplierOrder.loadItems || []).map((item) => (freightById.has(item.id)
        ? { ...item, freightPLN: freightById.get(item.id) ?? null }
        : item)),
    })),
  };
}
